// src/components/page-sections/HeaderDividedSection.js
import React, { useState, useEffect } from "react";
import LazyImage from "../../LazyImage";

const HeaderDividedSection = ({ content }) => {
  const {
    // Text content
    title = "Freshly <span>Baked,<br/> Daily</span> Served",
    subtitle = "Our bakery and pastry products are produced each day for the best quality and taste.",
    primaryButtonText = "View Our Menu",
    primaryButtonLink = "/menu",
    secondaryButtonText = "Order Online",
    secondaryButtonLink = "https://anorder.ca/1781/paneformaggio",
    hoursText = "Open Today: <span>7:00 AM - 10:00 PM</span>",

    // Colors
    leftBackgroundColor = "#f8f3e9",
    rightBackgroundColor = "#5c3618",
    titleColor = "#5c3618",
    highlightColor = "#d17b34",
    subtitleColor = "#6b5c4d",
    primaryButtonBackgroundColor = "#d17b34",
    primaryButtonTextColor = "#ffffff",
    secondaryButtonBorderColor = "#d17b34",
    secondaryButtonTextColor = "#d17b34",
    secondaryButtonHoverBackgroundColor = "#d17b34",
    secondaryButtonHoverTextColor = "#ffffff",
    hoursBadgeBackgroundColor = "#ffffff",
    hoursBadgeTextColor = "#5c3618",
    hoursBadgeHighlightColor = "#d17b34",
    overlayColor = "rgba(92, 54, 24, 0.8)",

    // Images
    mainImage = "/images/pastry/bread-hero.webp",
    breadIcon = "/images/pastry/bread-icon.png",
    floatingImage1 = "/images/pastry/croissant.png",
    floatingImage2 = "/images/pastry/bread.png",

    // Overlay card
    overlayCardTitle = "Try Our Specialty",
    overlayCardText = "Mix Berry Coffee Cake fresh throughout the day",
    overlayCardTagline = "→ Featured Today",
    overlayCardBackgroundColor = "rgba(255, 255, 255, 0.9)",
    overlayCardTitleColor = "#5c3618",
    overlayCardTextColor = "#6b5c4d",
    overlayCardTaglineColor = "#d17b34",

    // Animation and sizing
    leftColumnWidth = "1/2",
    rightColumnWidth = "1/2",
    enableAnimations = true,
    floatingImage1Visible = true,
    floatingImage2Visible = true,
    overlayCardVisible = true,
    iconVisible = true,
    hoursBadgeVisible = true,

    // Bottom categories
    showCategories = true,
    categoryItems = [],
    categoryBackgroundColor = "#5c3618",
    categoryTextColor = "#ffffff",
    categoryIconBackgroundColor = "#ffffff",
    thirdButtonText,
    thirdButtonLink,
    fourthButtonText,
    fourthButtonLink,
    buttonStyle = "default",
    buttonHeight = "",
    mobileButtonLayout = "horizontal",
    buttonLayoutExtended = "default",
    primaryButtonIcon = "",
    secondaryButtonIcon = "",
    thirdButtonIcon = "",
    fourthButtonIcon = "",
    buttonIconPosition = "before-text",
    buttonIconSize = "40px",

    //for left bacground & button
    // Left Section background props
    leftSectionType = "color",
    leftBackgroundImage = "",
    leftBackgroundVideo = "",
    leftOverlayEnabled = false,
    leftOverlayColor = "rgba(248, 243, 233, 0.8)",
    leftOverlayOpacity = "0.8",

    // Button background props
    primaryButtonBgType = "solid",
    primaryButtonOpacity = "1",
    primaryButtonGradientFrom = "",
    primaryButtonGradientTo = "",
    primaryButtonGradientDirection = "to right",

    secondaryButtonBgType = "transparent",
    secondaryButtonOpacity = "1",
    secondaryButtonBackgroundColor = "transparent",
    secondaryButtonGradientFrom = "",
    secondaryButtonGradientTo = "",
    secondaryButtonGradientDirection = "to right",

    thirdButtonBgType = "transparent",
    thirdButtonOpacity = "1",
    thirdButtonBackgroundColor = "transparent",
    thirdButtonGradientFrom = "",
    thirdButtonGradientTo = "",
    thirdButtonGradientDirection = "to right",

    fourthButtonBgType = "solid",
    fourthButtonOpacity = "1",
    fourthButtonBackgroundColor = "",
    fourthButtonGradientFrom = "",
    fourthButtonGradientTo = "",
    fourthButtonGradientDirection = "to right",

    contentAlignment = "center", // Add this line to define contentAlignment with a default value
  } = content || {};

  // For responsive behavior
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    const checkTablet = () => {
      setIsTablet(window.innerWidth < 1024 && window.innerWidth >= 768);
    };

    checkMobile();
    checkTablet();
    window.addEventListener("resize", checkMobile);
    window.addEventListener("resize", checkTablet);

    return () => {
      window.removeEventListener("resize", checkMobile);
      window.removeEventListener("resize", checkTablet);
    };
  }, []);

  // Parse HTML in title string to allow for highlights
  const renderTitle = () => {
    if (!title) return null;

    // Replace <span> tags with highlighted spans
    const parts = title.split(/<span>(.*?)<\/span>/);
    if (parts.length === 1)
      return <span style={{ color: titleColor }}>{title}</span>;

    return (
      <>
        <span style={{ color: titleColor }}>{parts[0]}</span>
        <span className="text-highlight" style={{ color: highlightColor }}>
          {parts[1].replace(/<br\/>/g, "")}
        </span>
        <span style={{ color: titleColor }}>{parts[2] || ""}</span>
      </>
    );
  };

  // Parse hours text to highlight span content
  const renderHoursText = () => {
    if (!hoursText) return null;

    const parts = hoursText.split(/<span>(.*?)<\/span>/);
    if (parts.length === 1)
      return <span style={{ color: hoursBadgeTextColor }}>{hoursText}</span>;

    return (
      <>
        <span style={{ color: hoursBadgeTextColor }}>{parts[0]}</span>
        <span style={{ color: hoursBadgeHighlightColor }}>{parts[1]}</span>
        <span style={{ color: hoursBadgeTextColor }}>{parts[2] || ""}</span>
      </>
    );
  };

  return (
    <div className="relative">
      {/* Main hero with split design */}
      <div className="flex flex-col lg:flex-row min-h-[85vh]">
        {/* Left side - Content */}
        {/* <div
          className={`lg:w-${leftColumnWidth} flex items-center justify-center p-8 lg:p-16 relative`}
          style={{
            backgroundColor:
              leftSectionType === "color" ? leftBackgroundColor : "transparent",
            position: "relative",
          }}
        > */}
        <div
          className={`${
            leftColumnWidth === "full"
              ? "lg:w-full pb-16"
              : `lg:w-${leftColumnWidth}`
          } flex items-center ${
            contentAlignment === "center" && leftColumnWidth === "full"
              ? "justify-center"
              : "justify-center"
          } p-8 lg:p-16 relative`}
          style={{
            backgroundColor:
              leftSectionType === "color" ? leftBackgroundColor : "transparent",
            position: "relative",
          }}
        >
          {/* Background media (image or video) */}
          {leftSectionType === "image" && leftBackgroundImage && (
            <div
              className="absolute inset-0 w-full h-full z-0"
              style={{
                backgroundImage: `url(${leftBackgroundImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            ></div>
          )}

          {leftSectionType === "video" && leftBackgroundVideo && (
            <video
              className="absolute inset-0 w-full h-full object-cover z-0"
              src={leftBackgroundVideo}
              autoPlay
              loop
              muted
              playsInline
            ></video>
          )}

          {/* Overlay for better text visibility on image/video backgrounds */}
          {(leftSectionType === "image" || leftSectionType === "video") &&
            leftOverlayEnabled && (
              <div
                className="absolute inset-0 w-full h-full z-1"
                style={{
                  backgroundColor:
                    leftOverlayColor || "rgba(248, 243, 233, 0.8)",
                  opacity: leftOverlayOpacity || "0.8",
                }}
              ></div>
            )}

          {/* Content container - layer on top of background */}
          <div
            className={`${
              leftColumnWidth === "full" && contentAlignment === "center"
                ? "text-center mx-auto"
                : "max-w-full"
            } relative z-2`}
            style={{
              marginTop: buttonLayoutExtended === "extended" ? "-80px" : "0",
              maxWidth:
                leftColumnWidth === "full" && contentAlignment === "center"
                  ? "800px"
                  : "none",
            }}
          >
            <div
              style={{
                marginTop:
                  buttonLayoutExtended === "extended" && !isMobile && !isTablet
                    ? "-200px"
                    : "0",
                position: "relative",
              }}
            >
              {iconVisible && (
                <div className="mb-4 inline-block">
                  <img
                    src={breadIcon}
                    alt="Section Icon"
                    className="h-12 w-auto"
                  />
                </div>
              )}

              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 leading-tight"
                dangerouslySetInnerHTML={{
                  __html: title.replace(
                    /<span>(.*?)<\/span>/g,
                    `<span style="color:${highlightColor}">$1</span>`
                  ),
                }}
              />

              <p
                className="text-lg mb-8 leading-relaxed"
                style={{ color: subtitleColor }}
              >
                {subtitle}
              </p>
            </div>
            {/* <div className="flex flex-wrap gap-4">
              <a 
                href={primaryButtonLink} 
                className="px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
                style={{ 
                  backgroundColor: primaryButtonBackgroundColor, 
                  color: primaryButtonTextColor
                }}
              >
                {primaryButtonText}
              </a>
              
              <a 
                href={secondaryButtonLink}
                className="px-8 py-4 border-2 rounded-full font-medium text-lg transition-all duration-300"
                style={{ 
                  borderColor: secondaryButtonBorderColor, 
                  color: secondaryButtonTextColor,
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = secondaryButtonHoverBackgroundColor;
                  e.currentTarget.style.color = secondaryButtonHoverTextColor;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = secondaryButtonTextColor;
                }}
              >
                {secondaryButtonText}
              </a>
            </div> */}
            {/* Buttons container with conditional styling */}
            <div
              className={`${
                isMobile && mobileButtonLayout === "vertical"
                  ? "flex flex-col"
                  : "flex flex-wrap"
              } ${
                buttonLayoutExtended === "extended" ? "absolute z-10" : ""
              } gap-4 ${enableAnimations ? "animate-zoom-slow" : ""}`}
              style={{
                position:
                  buttonLayoutExtended === "extended" ? "absolute" : "relative",
                marginTop: buttonLayoutExtended === "extended" ? "0" : "",
                top: buttonLayoutExtended === "extended" ? "50%" : "",
                left: buttonLayoutExtended === "extended" ? "0" : "",
                right: buttonLayoutExtended === "extended" ? "0" : "",
                transform:
                  buttonLayoutExtended === "extended" ? "translateY(-50%)" : "",
                width: buttonLayoutExtended === "extended" ? "100%" : "",
                display: "flex",
                justifyContent:
                  buttonLayoutExtended === "extended"
                    ? "center"
                    : isMobile
                    ? "center"
                    : "flex-start",
                alignItems: "center",
              }}
            >
              <a
                href={primaryButtonLink}
                className={`${
                  buttonStyle === "square" ? "rounded-lg" : "rounded-full"
                } font-medium text-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105`}
                style={{
                  backgroundColor:
                    primaryButtonBgType === "solid"
                      ? primaryButtonBackgroundColor
                      : primaryButtonBgType === "transparent"
                      ? "transparent"
                      : "none",
                  background:
                    primaryButtonBgType === "gradient"
                      ? `linear-gradient(${primaryButtonGradientDirection}, ${
                          primaryButtonGradientFrom ||
                          primaryButtonBackgroundColor
                        }, ${primaryButtonGradientTo || "#e8924a"})`
                      : "",
                  opacity: primaryButtonOpacity,
                  color: primaryButtonTextColor,
                  padding: buttonStyle === "square" ? "1.5rem" : "1rem 2rem",
                  height:
                    buttonHeight ||
                    (buttonStyle === "square" ? "80px" : "auto"),
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: buttonStyle === "square" ? buttonHeight : "auto",
                  flexDirection:
                    buttonIconPosition === "top-center" ? "column" : "row",
                  gap: "8px",
                  width:
                    isMobile && mobileButtonLayout !== "vertical"
                      ? "calc(50% - 8px)"
                      : "auto",
                }}
              >
                {primaryButtonIcon && (
                  <img
                    src={primaryButtonIcon}
                    alt=""
                    style={{
                      height: buttonIconSize,
                      width: "auto",
                      marginRight:
                        buttonIconPosition === "before-text" ? "8px" : "0",
                      marginBottom:
                        buttonIconPosition === "top-center" ? "4px" : "0",
                    }}
                  />
                )}
                {primaryButtonText}
              </a>

              <a
                href={secondaryButtonLink}
                className={`border-2 ${
                  buttonStyle === "square" ? "rounded-lg" : "rounded-full"
                } font-medium text-lg transition-all duration-300`}
                style={{
                  borderColor: secondaryButtonBorderColor,
                  backgroundColor:
                    secondaryButtonBgType === "solid"
                      ? secondaryButtonBackgroundColor
                      : secondaryButtonBgType === "transparent"
                      ? "transparent"
                      : "none",
                  background:
                    secondaryButtonBgType === "gradient"
                      ? `linear-gradient(${secondaryButtonGradientDirection}, ${
                          secondaryButtonGradientFrom ||
                          secondaryButtonBorderColor
                        }, ${secondaryButtonGradientTo || "#e8924a"})`
                      : "",
                  opacity: secondaryButtonOpacity,
                  color: secondaryButtonTextColor,
                  padding: buttonStyle === "square" ? "1.5rem" : "1rem 2rem",
                  height:
                    buttonHeight ||
                    (buttonStyle === "square" ? "80px" : "auto"),
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: buttonStyle === "square" ? buttonHeight : "auto",
                  flexDirection:
                    buttonIconPosition === "top-center" ? "column" : "row",
                  gap: "8px",
                  width:
                    isMobile && mobileButtonLayout !== "vertical"
                      ? "calc(50% - 8px)"
                      : "auto",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor =
                    secondaryButtonHoverBackgroundColor;
                  e.currentTarget.style.color = secondaryButtonHoverTextColor;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor =
                    secondaryButtonBgType === "solid"
                      ? secondaryButtonBackgroundColor
                      : "transparent";
                  e.currentTarget.style.color = secondaryButtonTextColor;
                }}
              >
                {secondaryButtonIcon && (
                  <img
                    src={secondaryButtonIcon}
                    alt=""
                    style={{
                      height: buttonIconSize,
                      width: "auto",
                      marginRight:
                        buttonIconPosition === "before-text" ? "8px" : "0",
                      marginBottom:
                        buttonIconPosition === "top-center" ? "4px" : "0",
                    }}
                  />
                )}
                {secondaryButtonText}
              </a>

              {/* Third button (optional) */}
              {thirdButtonText && (
                <a
                  href={thirdButtonLink || "#"}
                  className={`border-2 ${
                    buttonStyle === "square" ? "rounded-lg" : "rounded-full"
                  } font-medium text-lg transition-all duration-300`}
                  style={{
                    borderColor: secondaryButtonBorderColor,
                    backgroundColor:
                      thirdButtonBgType === "solid"
                        ? thirdButtonBackgroundColor
                        : thirdButtonBgType === "transparent"
                        ? "transparent"
                        : "none",
                    background:
                      thirdButtonBgType === "gradient"
                        ? `linear-gradient(${
                            thirdButtonGradientDirection || "to right"
                          }, ${
                            thirdButtonGradientFrom ||
                            secondaryButtonBorderColor
                          }, ${thirdButtonGradientTo || "#e8924a"})`
                        : "",
                    opacity: thirdButtonOpacity,
                    borderColor: secondaryButtonBorderColor,
                    color: secondaryButtonTextColor,
                    padding: buttonStyle === "square" ? "1.5rem" : "1rem 2rem",
                    height:
                      buttonHeight ||
                      (buttonStyle === "square" ? "80px" : "auto"),
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: buttonStyle === "square" ? buttonHeight : "auto",
                    flexDirection:
                      buttonIconPosition === "top-center" ? "column" : "row",
                    gap: "8px",
                    width:
                      isMobile && mobileButtonLayout !== "vertical"
                        ? "calc(50% - 8px)"
                        : "auto",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor =
                      secondaryButtonHoverBackgroundColor;
                    e.currentTarget.style.color = secondaryButtonHoverTextColor;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor =
                      thirdButtonBgType === "solid"
                        ? thirdButtonBackgroundColor
                        : "transparent";
                    e.currentTarget.style.color = secondaryButtonTextColor;
                  }}
                >
                  {thirdButtonIcon && (
                    <img
                      src={thirdButtonIcon}
                      alt=""
                      style={{
                        height: buttonIconSize,
                        width: "auto",
                        marginRight:
                          buttonIconPosition === "before-text" ? "8px" : "0",
                        marginBottom:
                          buttonIconPosition === "top-center" ? "4px" : "0",
                      }}
                    />
                  )}
                  {thirdButtonText}
                </a>
              )}

              {/* Fourth button (optional) */}
              {fourthButtonText && (
                <a
                  href={fourthButtonLink || "#"}
                  className={`${
                    buttonStyle === "square" ? "rounded-lg" : "rounded-full"
                  } font-medium text-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105`}
                  style={{
                    backgroundColor:
                      fourthButtonBgType === "solid"
                        ? fourthButtonBackgroundColor ||
                          primaryButtonBackgroundColor
                        : fourthButtonBgType === "transparent"
                        ? "transparent"
                        : "none",
                    background:
                      fourthButtonBgType === "gradient"
                        ? `linear-gradient(${
                            fourthButtonGradientDirection || "to right"
                          }, ${
                            fourthButtonGradientFrom ||
                            primaryButtonBackgroundColor
                          }, ${fourthButtonGradientTo || "#e8924a"})`
                        : "",
                    opacity: fourthButtonOpacity,
                    color: primaryButtonTextColor,
                    padding: buttonStyle === "square" ? "1.5rem" : "1rem 2rem",
                    height:
                      buttonHeight ||
                      (buttonStyle === "square" ? "80px" : "auto"),
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: buttonStyle === "square" ? buttonHeight : "auto",
                    flexDirection:
                      buttonIconPosition === "top-center" ? "column" : "row",
                    gap: "8px",
                    width:
                      isMobile && mobileButtonLayout !== "vertical"
                        ? "calc(50% - 8px)"
                        : "auto",
                  }}
                >
                  {fourthButtonIcon && (
                    <img
                      src={fourthButtonIcon}
                      alt=""
                      style={{
                        height: buttonIconSize,
                        width: "auto",
                        marginRight:
                          buttonIconPosition === "before-text" ? "8px" : "0",
                        marginBottom:
                          buttonIconPosition === "top-center" ? "4px" : "0",
                      }}
                    />
                  )}
                  {fourthButtonText}
                </a>
              )}
            </div>

            {/* For overlay card when left is full width */}
            {leftColumnWidth === "full" && overlayCardVisible && (
              <div
                className="my-8 p-2 md:p-6 rounded-lg shadow-lg max-w-lg mx-auto md:mx-0"
                style={{
                  opacity: "0.9", // Set opacity to 90%

                  backgroundColor: overlayCardBackgroundColor,
                  marginLeft: contentAlignment === "center" ? "auto" : "0",
                  marginRight: contentAlignment === "center" ? "auto" : "0",
                }}
              >
                <h3
                  className="text-xl font-semibold mb-2"
                  style={{ color: overlayCardTitleColor }}
                >
                  {overlayCardTitle}
                </h3>
                <div
                  style={{ color: overlayCardTextColor }}
                  dangerouslySetInnerHTML={{ __html: overlayCardText }}
                ></div>
                <div
                  className="mt-3 font-medium"
                  style={{ color: overlayCardTaglineColor }}
                >
                  {overlayCardTagline}
                </div>
              </div>
            )}

            {/* Hours badge */}
            {hoursBadgeVisible && (
              <div
                className="mt-2 inline-block px-6 py-3 rounded-full shadow-md"
                style={{
                  backgroundColor: hoursBadgeBackgroundColor,
                  border: `1px solid ${
                    leftBackgroundColor === "#ffffff"
                      ? "#f0e6d6"
                      : "transparent"
                  }`,
                }}
              >
                {renderHoursText()}
                {/* <p
                  dangerouslySetInnerHTML={{
                    __html: hoursText.replace(
                      /<span>(.*?)<\/span>/g,
                      `<span style="color:${hoursBadgeHighlightColor}">$1</span>`
                    ),
                  }}
                /> */}
              </div>
            )}
          </div>
        </div>

        {/* For overlay card when left is full width, add this after the main div */}

        {/* Right side - Image gallery with overlapping elements */}
        {/* <div
          className={`lg:w-${rightColumnWidth} relative min-h-[50vh] lg:min-h-full overflow-hidden`}
          style={{ backgroundColor: rightBackgroundColor }}
        > */}
        {leftColumnWidth !== "full" && (
          <div
            className={`lg:w-${rightColumnWidth} relative min-h-[50vh] lg:min-h-full overflow-hidden`}
            style={{ backgroundColor: rightBackgroundColor }}
          >
            {/* Main image */}
            <img
              src={mainImage}
              alt="Header Image"
              className="w-full h-full object-cover"
              style={{ opacity: 0.9 }}
            />

            {/* Floating elements */}
            {!isMobile && floatingImage1Visible && (
              <div
                className={`absolute top-1/4 left-1/4 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-xl ${
                  enableAnimations ? "animate-float-slow" : ""
                }`}
              >
                <img
                  src={floatingImage1}
                  alt="Floating Image 1"
                  className="h-16 w-16"
                />
              </div>
            )}

            {!isMobile && floatingImage2Visible && (
              <div
                className={`absolute bottom-1/4 right-1/4 transform translate-x-1/2 translate-y-1/2 bg-white rounded-full p-4 shadow-xl ${
                  enableAnimations ? "animate-float-medium" : ""
                }`}
              >
                <img
                  src={floatingImage2}
                  alt="Floating Image 2"
                  className="h-14 w-14"
                />
              </div>
            )}

            {/* Overlay text */}
            {overlayCardVisible && (
              <div
                className="absolute bottom-8 left-8 md:bottom-12 md:left-12 p-4 md:p-6 rounded-lg shadow-lg max-w-xs"
                style={{ backgroundColor: overlayCardBackgroundColor }}
              >
                <h3
                  className="text-xl font-semibold mb-2"
                  style={{ color: overlayCardTitleColor }}
                >
                  {overlayCardTitle}
                </h3>
                {/* <p style={{ color: overlayCardTextColor }}>
                {overlayCardText}
              </p> */}
                <div
                  style={{ color: overlayCardTextColor }}
                  dangerouslySetInnerHTML={{ __html: overlayCardText }}
                ></div>
                <div
                  className="mt-3 font-medium"
                  style={{ color: overlayCardTaglineColor }}
                >
                  {overlayCardTagline}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Feature categories scroll section */}
      {showCategories && (
        <div
          className="py-6 px-4 overflow-x-auto whitespace-nowrap"
          style={{ backgroundColor: categoryBackgroundColor }}
        >
          <div className="flex space-x-8 md:space-x-12 justify-start md:justify-center items-center">
            {categoryItems.map((item, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center transition-transform hover:scale-110"
              >
                <a
                  href={item.link || "#"}
                  className="flex flex-col items-center"
                >
                  <div
                    className="p-3 rounded-full shadow-md mb-2"
                    style={{ backgroundColor: categoryIconBackgroundColor }}
                  >
                    {item.imageUrl ? (
                      <LazyImage
                        src={item.imageUrl}
                        alt={item.name}
                        className="h-8 w-8"
                      />
                    ) : (
                      <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                    )}
                  </div>
                  <span
                    className="text-sm font-medium"
                    style={{ color: categoryTextColor }}
                  >
                    {item.name}
                  </span>
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add CSS for animations */}
      <style>{`
        @keyframes float-slow {
          0%,
          100% {
            transform: translate(-50%, -50%) translateY(0);
          }
          50% {
            transform: translate(-50%, -50%) translateY(-15px);
          }
        }

        @keyframes float-medium {
          0%,
          100% {
            transform: translate(50%, 50%) translateY(0);
          }
          50% {
            transform: translate(50%, 50%) translateY(-10px);
          }
        }

        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }

        .animate-float-medium {
          animation: float-medium 4s ease-in-out infinite;
        }
        @keyframes zoom-slow {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.04);
          }
        }

        @keyframes zoom-medium {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.07);
          }
        }

        @keyframes zoom-fast {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        .animate-zoom-slow {
          animation: zoom-slow 6s ease-in-out infinite;
        }

        .animate-zoom-medium {
          animation: zoom-medium 4s ease-in-out infinite;
        }

        .animate-zoom-fast {
          animation: zoom-fast 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default HeaderDividedSection;

// //before 100%
// // src/components/page-sections/HeaderDividedSection.js
// import React, { useState, useEffect } from "react";
// import LazyImage from "../../LazyImage";

// const HeaderDividedSection = ({ content }) => {
//   const {
//     // Text content
//     title = "Freshly <span>Baked,<br/> Daily</span> Served",
//     subtitle = "Our bakery and pastry products are produced each day for the best quality and taste.",
//     primaryButtonText = "View Our Menu",
//     primaryButtonLink = "/menu",
//     secondaryButtonText = "Order Online",
//     secondaryButtonLink = "https://anorder.ca/1781/paneformaggio",
//     hoursText = "Open Today: <span>7:00 AM - 10:00 PM</span>",

//     // Colors
//     leftBackgroundColor = "#f8f3e9",
//     rightBackgroundColor = "#5c3618",
//     titleColor = "#5c3618",
//     highlightColor = "#d17b34",
//     subtitleColor = "#6b5c4d",
//     primaryButtonBackgroundColor = "#d17b34",
//     primaryButtonTextColor = "#ffffff",
//     secondaryButtonBorderColor = "#d17b34",
//     secondaryButtonTextColor = "#d17b34",
//     secondaryButtonHoverBackgroundColor = "#d17b34",
//     secondaryButtonHoverTextColor = "#ffffff",
//     hoursBadgeBackgroundColor = "#ffffff",
//     hoursBadgeTextColor = "#5c3618",
//     hoursBadgeHighlightColor = "#d17b34",
//     overlayColor = "rgba(92, 54, 24, 0.8)",

//     // Images
//     mainImage = "/images/pastry/bread-hero.webp",
//     breadIcon = "/images/pastry/bread-icon.png",
//     floatingImage1 = "/images/pastry/croissant.png",
//     floatingImage2 = "/images/pastry/bread.png",

//     // Overlay card
//     overlayCardTitle = "Try Our Specialty",
//     overlayCardText = "Mix Berry Coffee Cake fresh throughout the day",
//     overlayCardTagline = "→ Featured Today",
//     overlayCardBackgroundColor = "rgba(255, 255, 255, 0.9)",
//     overlayCardTitleColor = "#5c3618",
//     overlayCardTextColor = "#6b5c4d",
//     overlayCardTaglineColor = "#d17b34",

//     // Animation and sizing
//     leftColumnWidth = "1/2",
//     rightColumnWidth = "1/2",
//     enableAnimations = true,
//     floatingImage1Visible = true,
//     floatingImage2Visible = true,
//     overlayCardVisible = true,
//     iconVisible = true,
//     hoursBadgeVisible = true,

//     // Bottom categories
//     showCategories = true,
//     categoryItems = [],
//     categoryBackgroundColor = "#5c3618",
//     categoryTextColor = "#ffffff",
//     categoryIconBackgroundColor = "#ffffff",
//     thirdButtonText,
//     thirdButtonLink,
//     fourthButtonText,
//     fourthButtonLink,
//     buttonStyle = "default",
//     buttonHeight = "",
//     mobileButtonLayout = "horizontal",
//     buttonLayoutExtended = "default",
//     primaryButtonIcon = "",
//     secondaryButtonIcon = "",
//     thirdButtonIcon = "",
//     fourthButtonIcon = "",
//     buttonIconPosition = "before-text",
//     buttonIconSize = "40px",

//     //for left bacground & button
//     // Left Section background props
//     leftSectionType = "color",
//     leftBackgroundImage = "",
//     leftBackgroundVideo = "",
//     leftOverlayEnabled = false,
//     leftOverlayColor = "rgba(248, 243, 233, 0.8)",
//     leftOverlayOpacity = "0.8",

//     // Button background props
//     primaryButtonBgType = "solid",
//     primaryButtonOpacity = "1",
//     primaryButtonGradientFrom = "",
//     primaryButtonGradientTo = "",
//     primaryButtonGradientDirection = "to right",

//     secondaryButtonBgType = "transparent",
//     secondaryButtonOpacity = "1",
//     secondaryButtonBackgroundColor = "transparent",
//     secondaryButtonGradientFrom = "",
//     secondaryButtonGradientTo = "",
//     secondaryButtonGradientDirection = "to right",

//     thirdButtonBgType = "transparent",
//     thirdButtonOpacity = "1",
//     thirdButtonBackgroundColor = "transparent",
//     thirdButtonGradientFrom = "",
//     thirdButtonGradientTo = "",
//     thirdButtonGradientDirection = "to right",

//     fourthButtonBgType = "solid",
//     fourthButtonOpacity = "1",
//     fourthButtonBackgroundColor = "",
//     fourthButtonGradientFrom = "",
//     fourthButtonGradientTo = "",
//     fourthButtonGradientDirection = "to right",
//   } = content || {};

//   // For responsive behavior
//   const [isMobile, setIsMobile] = useState(false);
//   const [isTablet, setIsTablet] = useState(false);

//   useEffect(() => {
//     const checkMobile = () => {
//       setIsMobile(window.innerWidth < 768);
//     };
//     const checkTablet = () => {
//       setIsTablet(window.innerWidth < 1024 && window.innerWidth >= 768);
//     };

//     checkMobile();
//     checkTablet();
//     window.addEventListener("resize", checkMobile);
//     window.addEventListener("resize", checkTablet);

//     return () => {
//       window.removeEventListener("resize", checkMobile);
//       window.removeEventListener("resize", checkTablet);
//     };
//   }, []);

//   // Parse HTML in title string to allow for highlights
//   const renderTitle = () => {
//     if (!title) return null;

//     // Replace <span> tags with highlighted spans
//     const parts = title.split(/<span>(.*?)<\/span>/);
//     if (parts.length === 1)
//       return <span style={{ color: titleColor }}>{title}</span>;

//     return (
//       <>
//         <span style={{ color: titleColor }}>{parts[0]}</span>
//         <span className="text-highlight" style={{ color: highlightColor }}>
//           {parts[1].replace(/<br\/>/g, "")}
//         </span>
//         <span style={{ color: titleColor }}>{parts[2] || ""}</span>
//       </>
//     );
//   };

//   // Parse hours text to highlight span content
//   const renderHoursText = () => {
//     if (!hoursText) return null;

//     const parts = hoursText.split(/<span>(.*?)<\/span>/);
//     if (parts.length === 1)
//       return <span style={{ color: hoursBadgeTextColor }}>{hoursText}</span>;

//     return (
//       <>
//         <span style={{ color: hoursBadgeTextColor }}>{parts[0]}</span>
//         <span style={{ color: hoursBadgeHighlightColor }}>{parts[1]}</span>
//         <span style={{ color: hoursBadgeTextColor }}>{parts[2] || ""}</span>
//       </>
//     );
//   };

//   return (
//     <div className="relative">
//       {/* Main hero with split design */}
//       <div className="flex flex-col lg:flex-row min-h-[85vh]">
//         {/* Left side - Content */}
//         <div
//           className={`lg:w-${leftColumnWidth} flex items-center justify-center p-8 lg:p-16 relative`}
//           style={{
//             backgroundColor:
//               leftSectionType === "color" ? leftBackgroundColor : "transparent",
//             position: "relative",
//           }}
//         >
//           {/* Background media (image or video) */}
//           {leftSectionType === "image" && leftBackgroundImage && (
//             <div
//               className="absolute inset-0 w-full h-full z-0"
//               style={{
//                 backgroundImage: `url(${leftBackgroundImage})`,
//                 backgroundSize: "cover",
//                 backgroundPosition: "center",
//               }}
//             ></div>
//           )}

//           {leftSectionType === "video" && leftBackgroundVideo && (
//             <video
//               className="absolute inset-0 w-full h-full object-cover z-0"
//               src={leftBackgroundVideo}
//               autoPlay
//               loop
//               muted
//               playsInline
//             ></video>
//           )}

//           {/* Overlay for better text visibility on image/video backgrounds */}
//           {(leftSectionType === "image" || leftSectionType === "video") &&
//             leftOverlayEnabled && (
//               <div
//                 className="absolute inset-0 w-full h-full z-1"
//                 style={{
//                   backgroundColor:
//                     leftOverlayColor || "rgba(248, 243, 233, 0.8)",
//                   opacity: leftOverlayOpacity || "0.8",
//                 }}
//               ></div>
//             )}

//           {/* Content container - layer on top of background */}
//           <div
//             className="max-w-full relative z-2"
//             style={{
//               marginTop: buttonLayoutExtended === "extended" ? "-80px" : "0",
//             }}
//           >
//             <div
//               style={{
//                 marginTop:
//                   buttonLayoutExtended === "extended" && !isMobile && !isTablet
//                     ? "-200px"
//                     : "0",
//                 position: "relative",
//               }}
//             >
//               {iconVisible && (
//                 <div className="mb-4 inline-block">
//                   <img
//                     src={breadIcon}
//                     alt="Section Icon"
//                     className="h-12 w-auto"
//                   />
//                 </div>
//               )}

//               <h1
//                 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 leading-tight"
//                 dangerouslySetInnerHTML={{
//                   __html: title.replace(
//                     /<span>(.*?)<\/span>/g,
//                     `<span style="color:${highlightColor}">$1</span>`
//                   ),
//                 }}
//               />

//               <p
//                 className="text-lg mb-8 leading-relaxed"
//                 style={{ color: subtitleColor }}
//               >
//                 {subtitle}
//               </p>
//             </div>
//             {/* <div className="flex flex-wrap gap-4">
//               <a
//                 href={primaryButtonLink}
//                 className="px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
//                 style={{
//                   backgroundColor: primaryButtonBackgroundColor,
//                   color: primaryButtonTextColor
//                 }}
//               >
//                 {primaryButtonText}
//               </a>

//               <a
//                 href={secondaryButtonLink}
//                 className="px-8 py-4 border-2 rounded-full font-medium text-lg transition-all duration-300"
//                 style={{
//                   borderColor: secondaryButtonBorderColor,
//                   color: secondaryButtonTextColor,
//                 }}
//                 onMouseOver={(e) => {
//                   e.currentTarget.style.backgroundColor = secondaryButtonHoverBackgroundColor;
//                   e.currentTarget.style.color = secondaryButtonHoverTextColor;
//                 }}
//                 onMouseOut={(e) => {
//                   e.currentTarget.style.backgroundColor = 'transparent';
//                   e.currentTarget.style.color = secondaryButtonTextColor;
//                 }}
//               >
//                 {secondaryButtonText}
//               </a>
//             </div> */}
//             {/* Buttons container with conditional styling */}
//             <div
//               className={`${
//                 isMobile && mobileButtonLayout === "vertical"
//                   ? "flex flex-col"
//                   : "flex flex-wrap"
//               } ${
//                 buttonLayoutExtended === "extended" ? "absolute z-10" : ""
//               } gap-4 ${enableAnimations ? "animate-zoom-slow" : ""}`}
//               style={{
//                 position:
//                   buttonLayoutExtended === "extended" ? "absolute" : "relative",
//                 marginTop: buttonLayoutExtended === "extended" ? "0" : "",
//                 top: buttonLayoutExtended === "extended" ? "50%" : "",
//                 left: buttonLayoutExtended === "extended" ? "0" : "",
//                 right: buttonLayoutExtended === "extended" ? "0" : "",
//                 transform:
//                   buttonLayoutExtended === "extended" ? "translateY(-50%)" : "",
//                 width: buttonLayoutExtended === "extended" ? "100%" : "",
//                 display: "flex",
//                 justifyContent:
//                   buttonLayoutExtended === "extended"
//                     ? "center"
//                     : isMobile
//                     ? "center"
//                     : "flex-start",
//                 alignItems: "center",
//               }}
//             >
//               <a
//                 href={primaryButtonLink}
//                 className={`${
//                   buttonStyle === "square" ? "rounded-lg" : "rounded-full"
//                 } font-medium text-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105`}
//                 style={{
//                   backgroundColor:
//                     primaryButtonBgType === "solid"
//                       ? primaryButtonBackgroundColor
//                       : primaryButtonBgType === "transparent"
//                       ? "transparent"
//                       : "none",
//                   background:
//                     primaryButtonBgType === "gradient"
//                       ? `linear-gradient(${primaryButtonGradientDirection}, ${
//                           primaryButtonGradientFrom ||
//                           primaryButtonBackgroundColor
//                         }, ${primaryButtonGradientTo || "#e8924a"})`
//                       : "",
//                   opacity: primaryButtonOpacity,
//                   color: primaryButtonTextColor,
//                   padding: buttonStyle === "square" ? "1.5rem" : "1rem 2rem",
//                   height:
//                     buttonHeight ||
//                     (buttonStyle === "square" ? "80px" : "auto"),
//                   display: "inline-flex",
//                   alignItems: "center",
//                   justifyContent: "center",
//                   minWidth: buttonStyle === "square" ? buttonHeight : "auto",
//                   flexDirection:
//                     buttonIconPosition === "top-center" ? "column" : "row",
//                   gap: "8px",
//                   width:
//                     isMobile && mobileButtonLayout !== "vertical"
//                       ? "calc(50% - 8px)"
//                       : "auto",
//                 }}
//               >
//                 {primaryButtonIcon && (
//                   <img
//                     src={primaryButtonIcon}
//                     alt=""
//                     style={{
//                       height: buttonIconSize,
//                       width: "auto",
//                       marginRight:
//                         buttonIconPosition === "before-text" ? "8px" : "0",
//                       marginBottom:
//                         buttonIconPosition === "top-center" ? "4px" : "0",
//                     }}
//                   />
//                 )}
//                 {primaryButtonText}
//               </a>

//               <a
//                 href={secondaryButtonLink}
//                 className={`border-2 ${
//                   buttonStyle === "square" ? "rounded-lg" : "rounded-full"
//                 } font-medium text-lg transition-all duration-300`}
//                 style={{
//                   borderColor: secondaryButtonBorderColor,
//                   backgroundColor:
//                     secondaryButtonBgType === "solid"
//                       ? secondaryButtonBackgroundColor
//                       : secondaryButtonBgType === "transparent"
//                       ? "transparent"
//                       : "none",
//                   background:
//                     secondaryButtonBgType === "gradient"
//                       ? `linear-gradient(${secondaryButtonGradientDirection}, ${
//                           secondaryButtonGradientFrom ||
//                           secondaryButtonBorderColor
//                         }, ${secondaryButtonGradientTo || "#e8924a"})`
//                       : "",
//                   opacity: secondaryButtonOpacity,
//                   color: secondaryButtonTextColor,
//                   padding: buttonStyle === "square" ? "1.5rem" : "1rem 2rem",
//                   height:
//                     buttonHeight ||
//                     (buttonStyle === "square" ? "80px" : "auto"),
//                   display: "inline-flex",
//                   alignItems: "center",
//                   justifyContent: "center",
//                   minWidth: buttonStyle === "square" ? buttonHeight : "auto",
//                   flexDirection:
//                     buttonIconPosition === "top-center" ? "column" : "row",
//                   gap: "8px",
//                   width:
//                     isMobile && mobileButtonLayout !== "vertical"
//                       ? "calc(50% - 8px)"
//                       : "auto",
//                 }}
//                 onMouseOver={(e) => {
//                   e.currentTarget.style.backgroundColor =
//                     secondaryButtonHoverBackgroundColor;
//                   e.currentTarget.style.color = secondaryButtonHoverTextColor;
//                 }}
//                 onMouseOut={(e) => {
//                   e.currentTarget.style.backgroundColor =
//                     secondaryButtonBgType === "solid"
//                       ? secondaryButtonBackgroundColor
//                       : "transparent";
//                   e.currentTarget.style.color = secondaryButtonTextColor;
//                 }}
//               >
//                 {secondaryButtonIcon && (
//                   <img
//                     src={secondaryButtonIcon}
//                     alt=""
//                     style={{
//                       height: buttonIconSize,
//                       width: "auto",
//                       marginRight:
//                         buttonIconPosition === "before-text" ? "8px" : "0",
//                       marginBottom:
//                         buttonIconPosition === "top-center" ? "4px" : "0",
//                     }}
//                   />
//                 )}
//                 {secondaryButtonText}
//               </a>

//               {/* Third button (optional) */}
//               {thirdButtonText && (
//                 <a
//                   href={thirdButtonLink || "#"}
//                   className={`border-2 ${
//                     buttonStyle === "square" ? "rounded-lg" : "rounded-full"
//                   } font-medium text-lg transition-all duration-300`}
//                   style={{
//                     borderColor: secondaryButtonBorderColor,
//                     backgroundColor:
//                       thirdButtonBgType === "solid"
//                         ? thirdButtonBackgroundColor
//                         : thirdButtonBgType === "transparent"
//                         ? "transparent"
//                         : "none",
//                     background:
//                       thirdButtonBgType === "gradient"
//                         ? `linear-gradient(${
//                             thirdButtonGradientDirection || "to right"
//                           }, ${
//                             thirdButtonGradientFrom ||
//                             secondaryButtonBorderColor
//                           }, ${thirdButtonGradientTo || "#e8924a"})`
//                         : "",
//                     opacity: thirdButtonOpacity,
//                     borderColor: secondaryButtonBorderColor,
//                     color: secondaryButtonTextColor,
//                     padding: buttonStyle === "square" ? "1.5rem" : "1rem 2rem",
//                     height:
//                       buttonHeight ||
//                       (buttonStyle === "square" ? "80px" : "auto"),
//                     display: "inline-flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     minWidth: buttonStyle === "square" ? buttonHeight : "auto",
//                     flexDirection:
//                       buttonIconPosition === "top-center" ? "column" : "row",
//                     gap: "8px",
//                     width:
//                       isMobile && mobileButtonLayout !== "vertical"
//                         ? "calc(50% - 8px)"
//                         : "auto",
//                   }}
//                   onMouseOver={(e) => {
//                     e.currentTarget.style.backgroundColor =
//                       secondaryButtonHoverBackgroundColor;
//                     e.currentTarget.style.color = secondaryButtonHoverTextColor;
//                   }}
//                   onMouseOut={(e) => {
//                     e.currentTarget.style.backgroundColor =
//                       thirdButtonBgType === "solid"
//                         ? thirdButtonBackgroundColor
//                         : "transparent";
//                     e.currentTarget.style.color = secondaryButtonTextColor;
//                   }}
//                 >
//                   {thirdButtonIcon && (
//                     <img
//                       src={thirdButtonIcon}
//                       alt=""
//                       style={{
//                         height: buttonIconSize,
//                         width: "auto",
//                         marginRight:
//                           buttonIconPosition === "before-text" ? "8px" : "0",
//                         marginBottom:
//                           buttonIconPosition === "top-center" ? "4px" : "0",
//                       }}
//                     />
//                   )}
//                   {thirdButtonText}
//                 </a>
//               )}

//               {/* Fourth button (optional) */}
//               {fourthButtonText && (
//                 <a
//                   href={fourthButtonLink || "#"}
//                   className={`${
//                     buttonStyle === "square" ? "rounded-lg" : "rounded-full"
//                   } font-medium text-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105`}
//                   style={{
//                     backgroundColor:
//                       fourthButtonBgType === "solid"
//                         ? fourthButtonBackgroundColor ||
//                           primaryButtonBackgroundColor
//                         : fourthButtonBgType === "transparent"
//                         ? "transparent"
//                         : "none",
//                     background:
//                       fourthButtonBgType === "gradient"
//                         ? `linear-gradient(${
//                             fourthButtonGradientDirection || "to right"
//                           }, ${
//                             fourthButtonGradientFrom ||
//                             primaryButtonBackgroundColor
//                           }, ${fourthButtonGradientTo || "#e8924a"})`
//                         : "",
//                     opacity: fourthButtonOpacity,
//                     color: primaryButtonTextColor,
//                     padding: buttonStyle === "square" ? "1.5rem" : "1rem 2rem",
//                     height:
//                       buttonHeight ||
//                       (buttonStyle === "square" ? "80px" : "auto"),
//                     display: "inline-flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     minWidth: buttonStyle === "square" ? buttonHeight : "auto",
//                     flexDirection:
//                       buttonIconPosition === "top-center" ? "column" : "row",
//                     gap: "8px",
//                     width:
//                       isMobile && mobileButtonLayout !== "vertical"
//                         ? "calc(50% - 8px)"
//                         : "auto",
//                   }}
//                 >
//                   {fourthButtonIcon && (
//                     <img
//                       src={fourthButtonIcon}
//                       alt=""
//                       style={{
//                         height: buttonIconSize,
//                         width: "auto",
//                         marginRight:
//                           buttonIconPosition === "before-text" ? "8px" : "0",
//                         marginBottom:
//                           buttonIconPosition === "top-center" ? "4px" : "0",
//                       }}
//                     />
//                   )}
//                   {fourthButtonText}
//                 </a>
//               )}
//             </div>

//             {/* Hours badge */}
//             {hoursBadgeVisible && (
//               <div
//                 className="mt-12 inline-block px-6 py-3 rounded-full shadow-md"
//                 style={{
//                   backgroundColor: hoursBadgeBackgroundColor,
//                   border: `1px solid ${
//                     leftBackgroundColor === "#ffffff"
//                       ? "#f0e6d6"
//                       : "transparent"
//                   }`,
//                 }}
//               >
//                 {renderHoursText()}
//                 {/* <p
//                   dangerouslySetInnerHTML={{
//                     __html: hoursText.replace(
//                       /<span>(.*?)<\/span>/g,
//                       `<span style="color:${hoursBadgeHighlightColor}">$1</span>`
//                     ),
//                   }}
//                 /> */}
//               </div>
//             )}
//           </div>
//         </div>

//         {/* Right side - Image gallery with overlapping elements */}
//         <div
//           className={`lg:w-${rightColumnWidth} relative min-h-[50vh] lg:min-h-full overflow-hidden`}
//           style={{ backgroundColor: rightBackgroundColor }}
//         >
//           {/* Main image */}
//           <img
//             src={mainImage}
//             alt="Header Image"
//             className="w-full h-full object-cover"
//             style={{ opacity: 0.9 }}
//           />

//           {/* Floating elements */}
//           {!isMobile && floatingImage1Visible && (
//             <div
//               className={`absolute top-1/4 left-1/4 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-xl ${
//                 enableAnimations ? "animate-float-slow" : ""
//               }`}
//             >
//               <img
//                 src={floatingImage1}
//                 alt="Floating Image 1"
//                 className="h-16 w-16"
//               />
//             </div>
//           )}

//           {!isMobile && floatingImage2Visible && (
//             <div
//               className={`absolute bottom-1/4 right-1/4 transform translate-x-1/2 translate-y-1/2 bg-white rounded-full p-4 shadow-xl ${
//                 enableAnimations ? "animate-float-medium" : ""
//               }`}
//             >
//               <img
//                 src={floatingImage2}
//                 alt="Floating Image 2"
//                 className="h-14 w-14"
//               />
//             </div>
//           )}

//           {/* Overlay text */}
//           {overlayCardVisible && (
//             <div
//               className="absolute bottom-8 left-8 md:bottom-12 md:left-12 p-4 md:p-6 rounded-lg shadow-lg max-w-xs"
//               style={{ backgroundColor: overlayCardBackgroundColor }}
//             >
//               <h3
//                 className="text-xl font-semibold mb-2"
//                 style={{ color: overlayCardTitleColor }}
//               >
//                 {overlayCardTitle}
//               </h3>
//               {/* <p style={{ color: overlayCardTextColor }}>
//                 {overlayCardText}
//               </p> */}
//               <div
//                 style={{ color: overlayCardTextColor }}
//                 dangerouslySetInnerHTML={{ __html: overlayCardText }}
//               ></div>
//               <div
//                 className="mt-3 font-medium"
//                 style={{ color: overlayCardTaglineColor }}
//               >
//                 {overlayCardTagline}
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Feature categories scroll section */}
//       {showCategories && (
//         <div
//           className="py-6 px-4 overflow-x-auto whitespace-nowrap"
//           style={{ backgroundColor: categoryBackgroundColor }}
//         >
//           <div className="flex space-x-8 md:space-x-12 justify-start md:justify-center items-center">
//             {categoryItems.map((item, index) => (
//               <div
//                 key={index}
//                 className="flex flex-col items-center text-center transition-transform hover:scale-110"
//               >
//                 <a
//                   href={item.link || "#"}
//                   className="flex flex-col items-center"
//                 >
//                   <div
//                     className="p-3 rounded-full shadow-md mb-2"
//                     style={{ backgroundColor: categoryIconBackgroundColor }}
//                   >
//                     {item.imageUrl ? (
//                       <LazyImage
//                         src={item.imageUrl}
//                         alt={item.name}
//                         className="h-8 w-8"
//                       />
//                     ) : (
//                       <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
//                     )}
//                   </div>
//                   <span
//                     className="text-sm font-medium"
//                     style={{ color: categoryTextColor }}
//                   >
//                     {item.name}
//                   </span>
//                 </a>
//               </div>
//             ))}
//           </div>
//         </div>
//       )}

//       {/* Add CSS for animations */}
//       <style>{`
//         @keyframes float-slow {
//           0%,
//           100% {
//             transform: translate(-50%, -50%) translateY(0);
//           }
//           50% {
//             transform: translate(-50%, -50%) translateY(-15px);
//           }
//         }

//         @keyframes float-medium {
//           0%,
//           100% {
//             transform: translate(50%, 50%) translateY(0);
//           }
//           50% {
//             transform: translate(50%, 50%) translateY(-10px);
//           }
//         }

//         .animate-float-slow {
//           animation: float-slow 6s ease-in-out infinite;
//         }

//         .animate-float-medium {
//           animation: float-medium 4s ease-in-out infinite;
//         }
//         @keyframes zoom-slow {
//           0%,
//           100% {
//             transform: scale(1);
//           }
//           50% {
//             transform: scale(1.04);
//           }
//         }

//         @keyframes zoom-medium {
//           0%,
//           100% {
//             transform: scale(1);
//           }
//           50% {
//             transform: scale(1.07);
//           }
//         }

//         @keyframes zoom-fast {
//           0%,
//           100% {
//             transform: scale(1);
//           }
//           50% {
//             transform: scale(1.1);
//           }
//         }

//         .animate-zoom-slow {
//           animation: zoom-slow 6s ease-in-out infinite;
//         }

//         .animate-zoom-medium {
//           animation: zoom-medium 4s ease-in-out infinite;
//         }

//         .animate-zoom-fast {
//           animation: zoom-fast 3s ease-in-out infinite;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default HeaderDividedSection;
